import { Component } from '@theme/component';
import {
  ThemeEvents,
  VariantUpdateEvent,
  ZoomMediaSelectedEvent,
} from '@theme/events';

/**
 * Media Gallery Component
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent]
 * @property {import('./slideshow').Slideshow} [slideshow]
 * @property {HTMLElement[]} [media]
 */
export class MediaGallery extends Component {
  #controller;
  #zoomListenerBound = false;

  connectedCallback() {
    super.connectedCallback();

    const target = this.closest('.shopify-section, dialog');
    if (!target) return;

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    target.addEventListener(
      ThemeEvents.variantUpdate,
      this.#handleVariantUpdate,
      { signal }
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#controller = null;
  }

  /**
   * Replace gallery on variant update
   * @param {VariantUpdateEvent} event
   */
  #handleVariantUpdate = (event) => {
    const html = event?.detail?.data?.html;
    if (!html) return;

    const replacement = html.querySelector('media-gallery');
    if (!replacement) return;

    this.replaceWith(replacement);
  };

  /**
   * Lazily attach zoom listener (only when zoom is used)
   */
  #ensureZoomListener() {
    if (this.#zoomListenerBound) return;

    const zoomDialog = this.refs.zoomDialogComponent;
    if (!zoomDialog) return;

    zoomDialog.addEventListener(
      ThemeEvents.zoomMediaSelected,
      this.#handleZoomMediaSelected,
      { signal: this.#controller.signal }
    );

    this.#zoomListenerBound = true;
  }

  /**
   * Sync slideshow with zoom dialog
   * @param {ZoomMediaSelectedEvent} event
   */
  #handleZoomMediaSelected = (event) => {
    const index = event?.detail?.index;
    if (index == null) return;

    this.slideshow?.select(index, undefined, { animate: false });
  };

  /**
   * Open zoom dialog
   * @param {number} index
   * @param {PointerEvent} event
   */
  zoom(index, event) {
    this.#ensureZoomListener();
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /* ===== Getters ===== */

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
